// Code block of a chat answer with a copy button in its top-right corner.
// Replaces react-markdown's `pre`: frame, padding and font still come from the
// answer surface (`StreamingResponse`, `[&_pre]:…`), this only adds the button.

import { Check, Copy } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  type ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SPRING_PRESS, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";

export interface CodeBlockLabels {
  copy: string;
  copied: string;
}

export interface CodeBlockProps extends ComponentPropsWithoutRef<"pre"> {
  labels: CodeBlockLabels;
}

/** How long the check mark stays before the copy icon returns. */
const COPIED_MS = 1600;

export function CodeBlock({ labels, className, children, ...props }: CodeBlockProps) {
  const reduce = useReducedMotion() ?? false;
  const preRef = useRef<HTMLPreElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  // Controlled so the bubble stays open and switches to "Kopiert!" on click —
  // Radix would otherwise close it on pointer down.
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    // The rendered text, not the markdown source: no fence, no language tag.
    const text = (preRef.current?.textContent ?? "").replace(/\n$/, "");
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      return;
    }
    setCopied(true);
    setTooltipOpen(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
  }, []);

  const label = copied ? labels.copied : labels.copy;

  return (
    // The wrapper takes over the block margin so the button measures from the
    // frame's edge; right padding keeps the first line from running under it.
    <div className="relative my-3">
      <pre ref={preRef} className={cn("!my-0 pr-12", className)} {...props}>
        {children}
      </pre>
      <TooltipProvider delayDuration={300}>
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <motion.button
              type="button"
              aria-label={label}
              data-testid="chat-code-copy"
              onClick={handleCopy}
              onPointerDown={(event) => event.preventDefault()}
              whileTap={reduce ? undefined : { scale: 0.88 }}
              transition={SPRING_PRESS}
              className={cn(
                "absolute right-2 top-2 grid size-7 place-items-center rounded-md border border-border bg-background/80 text-muted-foreground outline-none backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
                copied && "text-foreground",
              )}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={copied ? "copied" : "copy"}
                  aria-hidden="true"
                  initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.5, filter: "blur(2px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.5, filter: "blur(2px)" }}
                  transition={reduce ? { duration: 0.1 } : SPRING_SWAP}
                  className="grid place-items-center"
                >
                  {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent className="text-sm font-medium">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
